import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import { GuestRequest } from '../../types';

interface GuestRequestsModalProps {
  isOpen: boolean;
  matchId: string;
  onClose: () => void;
  onRequestsUpdated: () => void;
}

const AVATAR_COLORS = ['#7C4DFF', '#FF6D00', '#00BCD4', '#E91E63'];

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'hace un momento';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `hace ${days}d`;
}

export const GuestRequestsModal: React.FC<GuestRequestsModalProps> = ({
  isOpen,
  matchId,
  onClose,
  onRequestsUpdated,
}) => {
  const [requests, setRequests] = useState<GuestRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchRequests = async () => {
    setLoading(true);
    setErrorMsg(null);
    const response = await apiRequest<GuestRequest[]>(`/api/matches/${matchId}/guest-requests`);
    if (response.data) {
      setRequests(Array.isArray(response.data) ? response.data.filter(r => r.status === 'PENDING') : []);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      fetchRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAction = async (requestId: string, action: 'approve' | 'reject') => {
    setProcessingIds(prev => new Set(prev).add(requestId));
    setErrorMsg(null);
    const response = await apiRequest(
      `/api/matches/${matchId}/guest-requests/${requestId}/${action}`,
      { method: 'POST' }
    );
    if (response.error) {
      setErrorMsg(response.error);
    } else {
      setRequests(prev => prev.filter(r => r.id !== requestId));
      onRequestsUpdated();
    }
    setProcessingIds(prev => {
      const next = new Set(prev);
      next.delete(requestId);
      return next;
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '16px',
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: '18px',
        border: '1px solid #33383B',
        padding: '24px',
        width: '100%',
        maxWidth: '560px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#FFFFFF' }}>
              Solicitudes de invitados
            </h2>
            {requests.length > 0 && (
              <span style={{
                backgroundColor: '#FF5722', color: '#FFF', borderRadius: '12px',
                minWidth: '24px', height: '24px', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: 700, padding: '0 6px',
              }}>
                {requests.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#B0BEC5', fontSize: '20px', lineHeight: 1, padding: '4px',
            }}
          >
            ✕
          </button>
        </div>

        {errorMsg && (
          <div style={{
            backgroundColor: 'rgba(255, 23, 68, 0.1)', border: '1px solid #FF1744',
            borderRadius: '8px', padding: '10px 14px', marginBottom: '12px',
            color: '#FF1744', fontSize: '13px',
          }}>
            {errorMsg}
          </div>
        )}

        {/* Content */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ paddingTop: '48px', textAlign: 'center', color: '#B0BEC5' }}>
              Cargando...
            </div>
          ) : requests.length === 0 ? (
            <div style={{ paddingTop: '48px', textAlign: 'center' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>👥</div>
              <p style={{ color: '#B0BEC5', margin: 0 }}>No hay solicitudes pendientes</p>
            </div>
          ) : (
            requests.map((request, index) => {
              const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];
              const isProcessing = processingIds.has(request.id);
              return (
                <div key={request.id} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  paddingTop: '12px', paddingBottom: '12px',
                  borderBottom: '1px solid #33383B',
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '50%',
                    backgroundColor: avatarColor, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ color: '#FFF', fontSize: '16px', fontWeight: 700 }}>
                      {request.guestName.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF' }}>
                        {request.guestName}
                      </span>
                      {request.playerId && (
                        <span style={{
                          backgroundColor: 'rgba(124, 77, 255, 0.15)',
                          border: '1px solid #7C4DFF', borderRadius: '4px',
                          padding: '1px 5px', fontSize: '9px', fontWeight: 700,
                          color: '#7C4DFF', letterSpacing: '0.3px',
                        }}>
                          REEMPLAZO
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', color: '#B0BEC5' }}>
                        {request.guestPosition} · {timeAgo(request.createdAt)}
                      </span>
                      <span style={{ fontSize: '12px', color: '#FFD700', letterSpacing: '1px' }}>
                        {'★'.repeat(request.guestRating)}{'☆'.repeat(5 - request.guestRating)}
                      </span>
                    </div>
                    <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#B0BEC5', fontStyle: 'italic' }}>
                      {request.playerId
                        ? `${request.requester.name} dice que es este jugador`
                        : `Propuesto por ${request.requester.name}`}
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button
                      onClick={() => handleAction(request.id, 'reject')}
                      disabled={isProcessing}
                      title="Rechazar"
                      style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        border: '1px solid #FF1744', backgroundColor: 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        opacity: isProcessing ? 0.5 : 1, color: '#FF1744', fontSize: '16px',
                      }}
                    >
                      ✕
                    </button>
                    <button
                      onClick={() => handleAction(request.id, 'approve')}
                      disabled={isProcessing}
                      title="Aprobar"
                      style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        border: 'none', backgroundColor: '#00E676',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        opacity: isProcessing ? 0.5 : 1, color: '#000', fontSize: '16px',
                        fontWeight: 700,
                      }}
                    >
                      ✓
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
